import * as XLSX from 'xlsx';
import { ExcelSheetData } from './types';

export function parseExcelFile (file: File): Promise<ExcelSheetData> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target!.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];

                const jsonData: string[][] = XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                    defval: '',
                    raw: false,
                });

                const rawJsonData: any[][] = XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                    defval: '',
                    raw: true,
                });

                // Extract cell formulas (SheetJS exposes them on `cell.f`). sheet_to_json discards
                // them, so we walk the sheet range manually, keeping the same row/column alignment
                // as `jsonData` (which starts at the range's first row).
                const formulaRows: string[][] = [];
                const ref = worksheet['!ref'];
                if (ref) {
                    const range = XLSX.utils.decode_range(ref);
                    for (let r = range.s.r; r <= range.e.r; r++) {
                        const formulaRow: string[] = [];
                        for (let c = range.s.c; c <= range.e.c; c++) {
                            const cell = worksheet[XLSX.utils.encode_cell({ r, c })];
                            formulaRow.push(cell && typeof cell.f === 'string' ? cell.f : '');
                        }
                        formulaRows.push(formulaRow);
                    }
                }

                const columnCount = jsonData.reduce((max, row) => Math.max(max, row.length), 0);

                const normalizedRows = jsonData.map(row => {
                    const normalized = [...row];
                    while (normalized.length < columnCount) {
                        normalized.push('');
                    }
                    return normalized.map(cell => String(cell ?? ''));
                });

                const normalizedRawRows = rawJsonData.map(row => {
                    const normalized = [...row];
                    while (normalized.length < columnCount) {
                        normalized.push('');
                    }
                    return normalized.map(cell => String(cell ?? ''));
                });

                const normalizedFormulaRows = formulaRows.map(row => {
                    const normalized = [...row];
                    while (normalized.length < columnCount) {
                        normalized.push('');
                    }
                    return normalized.map(cell => String(cell ?? ''));
                });

                resolve({
                    sheetName: firstSheetName,
                    rows: normalizedRows,
                    rawRows: normalizedRawRows,
                    formulaRows: normalizedFormulaRows,
                    columnCount,
                });
            } catch (err) {
                reject(err);
            }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsArrayBuffer(file);
    });
}

export function detectLineType (cellValue: string): 'S' | 'O' | null {
    const normalized = cellValue.trim().toUpperCase();
    if (['S', 'SECTION', 'SECÇÃO', 'SECAO', 'SECCION', 'SEC'].includes(normalized)) {
        return 'S';
    }
    if (['O', 'OBS', 'OBSERVAÇÃO', 'OBSERVACAO', 'OBSERVATION', 'NOTE', 'N'].includes(normalized)) {
        return 'O';
    }
    return null;
}
